import React from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { CardGiftcardRounded } from "@mui/icons-material";
import { getCurrentPersianMonthName, getCurrentPersianSeasonName, toPriceWithComma, toPriceWithoutComma } from "../../../helper/utils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import _CreditCategoriesSelector from "./_CreditCategoriesSelector";
import _BlockedSportsSelector from "./_BlockedSportsSelector";

const currentMonthName = getCurrentPersianMonthName();
const currentSeasonName = getCurrentPersianSeasonName();

const PRESET_NAMES = [
    `اعتبار ${currentMonthName}`,
    `اعتبار ${currentSeasonName}`,
    "هدیه تولد",
    "هدیه روز مرد",
    "هدیه روز زن",
    "هدیه شب یلدا"
];

const PRESET_AMOUNTS = [
    2000000,
    3000000,
    5000000,
    10000000,
];

const _AllocationFormFields = ({
    formData,
    setFormData,
    rawCreditAmount,
    usageCategories,
    handleCategoryToggle,
    blockedSportIds,
    setBlockedSportIds,
    sportsList,
    handleOpenConfirm,
}) => {
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                p: { xs: 2.5, md: 3.5 },
            }}
        >
            <Box component="form" onSubmit={handleOpenConfirm}>
                <Stack spacing={3.5}>
                    {/* Preset Package Names */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                            عنوان بسته اعتباری:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
                            {PRESET_NAMES.map((name) => (
                                <Chip
                                    key={name}
                                    label={name}
                                    clickable
                                    onClick={() => setFormData({ ...formData, Name: name })}
                                    color={formData.Name === name ? "primary" : "default"}
                                    variant={formData.Name === name ? "filled" : "outlined"}
                                    sx={{ fontWeight: 700 }}
                                />
                            ))}
                        </Stack>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="عنوان دلخواه"
                            value={formData.Name}
                            onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    </Box>

                    {/* Amount Section */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                            مبلغ اعتبار به ازای هر فرد (تومان):
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
                            {PRESET_AMOUNTS.map((amt) => (
                                <Chip
                                    key={amt}
                                    label={`${toPriceWithComma(amt)} تومان`}
                                    clickable
                                    onClick={() => setFormData({ ...formData, CreditAmount: String(amt) })}
                                    color={String(rawCreditAmount) === String(amt) ? "primary" : "default"}
                                    variant={String(rawCreditAmount) === String(amt) ? "filled" : "outlined"}
                                    sx={{ fontWeight: 700 }}
                                />
                            ))}
                        </Stack>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="مبلغ اعتبار"
                            value={toPriceWithComma(formData.CreditAmount)}
                            onChange={(e) => setFormData({ ...formData, CreditAmount: toPriceWithoutComma(e.target.value) })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">تومان</InputAdornment>,
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    </Box>

                    {/* Expiration Date */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                            مهلت استفاده (تاریخ انقضا):
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
                            <DatePicker
                                disablePast
                                value={formData.ExpireDate}
                                onChange={(newDate) => setFormData({ ...formData, ExpireDate: newDate })}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        sx: { '& .MuiOutlinedInput-root': { borderRadius: 3 } },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>

                    {/* Section: بخش‌های مصرف (UsageCategories) */}
                    <_CreditCategoriesSelector
                        usageCategories={usageCategories}
                        handleCategoryToggle={handleCategoryToggle}
                    />

                    {/* Section: ورزش‌های ممنوع (BlockedSportIds - Blocklist) */}
                    <_BlockedSportsSelector
                        usageCategories={usageCategories}
                        blockedSportIds={blockedSportIds}
                        setBlockedSportIds={setBlockedSportIds}
                        sportsList={sportsList}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            startIcon={<CardGiftcardRounded />}
                            sx={{
                                borderRadius: 3,
                                px: 4,
                                py: 1.2,
                                fontWeight: 800,
                                boxShadow: '0 8px 16px rgba(45, 107, 226, 0.25)',
                            }}
                        >
                            مرحله بعد و بررسی بودجه
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
};

export default _AllocationFormFields;
